/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  Children,
  cloneElement,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react'
import PropTypes from 'prop-types'

import { Throbber } from 'ui/throbber'
import { DefaultErrorHandler } from 'ui/data-loader/loader'

import { useListLoader } from 'components/use-loader/use-list-loader'

import { ORDER } from 'const/types'
import { DEFAULT_LIMIT, DEFAULT_ORDER, DEFAULT_PAGE } from 'const/list'
import { listFilters, listFiltersPermanent, listLimitsStorage } from 'api/storage'

import { ListContextProvider } from './list-context'

/**
 * Calls the <endpoint> with a single argument -
 * object that contains the rest of props (excluding children):
 * endpoint({ limit, page })

 * For every child, it passes the following props:
 * items
 * totalItems,
 * pending,
 * error,
 * page,
 * limit,
 */
export const ListLoaderProps = ({
  endpoint,
  children,
  pendingComponent: Pending,
  errorComponent: Error,
  limitName,
  filterName,
  permanent,
  ...props
}) => {
  const prevFilters = useRef('')
  const savedPage = useRef('')
  const savedLimit = useRef('')
  const queryParams = useMemo(() => {
    const { page, limit, ...filters } = permanent
      ? listFiltersPermanent.value?.[filterName] ?? {}
      : listFilters.value?.[filterName] ?? {}
    savedPage.current = page
    savedLimit.current = limit
    return { page: page || props.page, limit: limit || props.limit, filters }
  }, [])
  const [page, setPage] = useState(Number(queryParams.page) || DEFAULT_PAGE)
  const [order, setOrder] = useState(props.order ?? null)
  const [limit, setLimit] = useState(Number(queryParams.limit) || DEFAULT_LIMIT)
  const [filters, setFilters] = useState(queryParams.filters ?? props.filters ?? {})

  const setPageNumber = useCallback(
    (value) => {
      if (Number(value) && Number(value) !== page && !savedPage.current) {
        setPage(Number(value))
      }
      savedPage.current = ''
    },

    [page],
  )
  const setLimitNumber = useCallback(
    (value) => {
      if (Number(value) && Number(value) !== limit && !savedLimit.current) {
        setLimit(Number(value))
      }
      savedLimit.current = ''
    },
    [limit],
  )

  const setLimitWithStorageUpdate = useCallback((value) => {
    if (Number(value) !== limit && limitName) {
      const limits = listLimitsStorage.value
      listLimitsStorage.value = { ...limits, [limitName]: value }
    }
    setLimit(value)
  }, [])

  const setFiltersValues = useCallback(
    (values) => {
      if (JSON.stringify(values) !== JSON.stringify(filters)) {
        setFilters(values)
      }
    },
    [JSON.stringify(filters)],
  )

  useEffect(() => {
    setPageNumber(props.page)
  }, [props.page])

  useEffect(() => {
    setLimitNumber(props.limit)
  }, [props.limit])

  useEffect(() => {
    const hasFilters = Object.entries(props.filters).length
    const isFiltersClearing = prevFilters.current && !hasFilters
    if (isFiltersClearing || hasFilters) {
      setFiltersValues(props.filters)
    }
  }, [JSON.stringify(props.filters)])

  const { pending, error, result, refresh } = useListLoader(endpoint, {
    ...props,
    page,
    order,
    limit,
    filters: JSON.stringify(filters),
  })

  if (pending && !Array.isArray(result?.items)) return <Pending />
  if (error) return <Error error={error?.error || error} />

  const value = {
    ...result,
    pending,
    error,
    ...result,
    items: result?.items ?? null,
    totalItems: result?.totalItems ?? null,
    page,
    limit: Number(limit),
    order,
    filters,
  }

  const contextValue = {
    setPage: setPageNumber,
    setOrder,
    refresh,
    setLimit: setLimitWithStorageUpdate,
    setFilters: setFiltersValues,
  }

  return (
    <ListContextProvider value={contextValue}>
      {Children.map(
        children,
        (child) =>
          child &&
          cloneElement(child, {
            ...value,
            filters: { ...child.props.filters, ...value.filters },
            permanent: child.props.permanent ?? permanent,
            filterName: child.props.filterName ?? filterName,
          }),
      )}
    </ListContextProvider>
  )
}

ListLoaderProps.propTypes = {
  endpoint: PropTypes.func.isRequired,
  errorComponent: PropTypes.elementType,
  filterName: PropTypes.string,
  filters: PropTypes.object,
  limit: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  limitName: PropTypes.string,
  order: PropTypes.objectOf(PropTypes.oneOf(Object.values(ORDER))),
  page: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  pendingComponent: PropTypes.elementType,
  permanent: PropTypes.bool,
}

ListLoaderProps.defaultProps = {
  page: DEFAULT_PAGE,
  limit: DEFAULT_LIMIT,
  order: { createdAt: DEFAULT_ORDER },
  filters: {},
  pendingComponent: Throbber,
  errorComponent: DefaultErrorHandler,
}
